import React, { useCallback } from 'react';

import CheckSmall from '@rover/icons/svg/check-small.svg';
import XmarkSmall from '@rover/icons/svg/xmark-small.svg';
import useTheme from '@rover/kibble/themes/useTheme';

import Box from '../../../core/Box/Box';
import { ThemeBackgroundColors } from '../../../styles/themeTypes';
import { KibbleFormFieldComponentCommonProps } from '../../types';
import { stopPropagation } from '../utils';

import { ANIMATION_DURATION_MS } from './SwitchField.common';

export type Props = {
  fieldProps: KibbleFormFieldComponentCommonProps['fieldProps'] & {
    'aria-describedby'?: string;
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    id: string;
  };
  disabled?: boolean;
};

const SwitchFieldIcon = ({ fieldProps, disabled }: Props): JSX.Element => {
  const theme = useTheme();

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (disabled) {
        // hilariously, this is the best way (a11y ux-wise) to get a
        // readonly checkbox that you can't change the value of
        e.target.checked = !e.target.checked;
      } else {
        // only call fieldProps.onChange if the input is not disabled
        fieldProps.onChange(e);
      }
    },
    [disabled, fieldProps]
  );

  const borderColor: string = disabled
    ? theme.colors.interactive_border.disabled.toString()
    : theme.colors.interactive_border.primary_base.toString();
  const circleColor: ThemeBackgroundColors | string = disabled
    ? theme.colors.interactive_text.disabled.toString()
    : theme.background.interactive.primary_active.toString();

  return (
    <Box position="relative">
      <Box
        {...fieldProps}
        webAs="input"
        webAsProps={{
          type: 'checkbox',
          readOnly: disabled,
          onChange: handleOnChange,
        }}
        role="switch"
        aria-disabled={disabled}
        onClick={stopPropagation} // prevents calling the wrapper click handler when this element is clicked
        height="32px"
        width="56px"
        borderRadius="round"
        borderWidth="2px"
        borderColor={borderColor}
        borderStyle="solid"
        sx={{
          appearance: 'none',
          outline: 'none !important',
          background: disabled ? theme.background.interactive.disabled : 'transparent',
          cursor: 'inherit', // match the cursor style of the parent
          mt: '-2px', // -2px here properly aligns this icon with the label

          // checked styles, we do this via CSS selectors to keep this input uncontrolled and more performant
          '&:checked': {
            background: disabled
              ? theme.background.interactive.disabled
              : theme.background.interactive.primary_active,
            borderColor: disabled
              ? theme.background.interactive.disabled
              : theme.background.interactive.primary_active,

            // targets the next sibling div, the circle
            '& + div': {
              background: disabled ? theme.textColor.disabled.toString() : 'primary',
              left: 28,

              // the first svg is the checkmark
              '& > svg': {
                display: 'block',
              },
              // the next svg is the xmark
              '& > svg + svg': {
                display: 'none',
              },
            },
          },
        }}
      />
      <Box
        height="24px"
        width="24px"
        borderRadius="round"
        position="absolute"
        sx={{
          background: circleColor,
          top: 2,
          left: 4,
          transition: `left ${ANIMATION_DURATION_MS}ms ease-in-out`,
        }}
      >
        <CheckSmall
          height="24px"
          width="24px"
          display="none"
          fill={
            disabled
              ? theme.background.interactive.disabled.toString()
              : theme.textColor.primary.toString()
          }
        />
        <XmarkSmall
          height="24px"
          width="24px"
          display="block"
          fill={
            disabled
              ? theme.background.interactive.disabled.toString()
              : theme.textColor.primary_inverse.toString()
          }
        />
      </Box>
    </Box>
  );
};

export default SwitchFieldIcon;
